import { Block } from '@components/Layout';
import ConditionallyWrapWithUrl from '@components/MiscComponents/ConditionallyWrapWithUrl';
import { Pagination } from '@components/Pagination';
import { Listing } from '@engelvoelkersamericas/api';
import { ChevronDown, ChevronUp } from '@icon/Chevrons';
import { ClickAwayListener } from '@mui/material';
import { useCurrency } from '@util/customHooks/useCurrency';
import { useMeasurement } from '@util/customHooks/useMeasurement';
import { useShop } from '@util/customHooks/useShop';
import { buildListUrl } from '@util/listings';
import { getSqFt } from '@util/propertiesMapping';
import { getCompiledAddressForDevelopments } from '@util/string';
import { useTranslations } from 'next-intl';
import { FC, ReactNode, useEffect, useState } from 'react';
import {
  ButtonGroup,
  DropdownContent,
  Filter,
  FilterDropdown,
  FilterLabel,
  FilterText,
  FilterToggle,
  Header,
  HistoryTable,
  HistoryTableMobile,
  Label,
  PaginationContainer,
  Tab,
  Text,
} from './styles';

interface DevelopmentUnitDetailsProps {
  eventHistory: {
    pastListings: Listing[];
    activeListings: Listing[];
  };
  resultsPerPage?: number;
  custom14?: any;
  custom15?: any;
  currentPage: number;
  handlePageSelect: (page: number) => null;
  totalResults: number;
  shopname?: string;
}

const DevelopmentUnitDetails: FC<DevelopmentUnitDetailsProps> = ({
  eventHistory,
  resultsPerPage = 10,
  custom14,
  custom15,
  currentPage,
  handlePageSelect,
  totalResults,
  shopname,
}) => {
  const t = useTranslations();
  const filters = [
    'Price (High to Low)',
    'Price (Low to High)',
    'Beds/Baths (Ascending)',
    'Beds/Baths (Descending)',
  ];

  const { getListingPrice } = useCurrency();
  const { convertArea } = useMeasurement();
  const { shop } = useShop();

  const [active, setActive] = useState('');
  const [activeFilter, setFilter] = useState(filters[0]);
  const [tabs, setTabs] = useState<string[]>([]);
  const [isOpenDropdown, setOpenDropdown] = useState(false);

  const renderBedBath = (listing: Listing): ReactNode => {
    const bedString = listing.BedroomsTotal ? (
      <span>
        {listing.BedroomsTotal} {t('Beds')}
      </span>
    ) : null;
    const bathString = listing.BathroomsTotal ? (
      <span>
        {listing.BathroomsTotal} {t('Baths')}
      </span>
    ) : null;
    return bedString || bathString ? (
      <Text>
        {bedString}
        {bedString && bathString && ', '}
        {bathString}
      </Text>
    ) : (
      <Text>{t('N/A')}</Text>
    );
  };

  const { activeListings, pastListings } = eventHistory;

  const getListingBelongsToCurrentStore = (listing: Listing) => {
    const listOfficeMlsId = listing.ListOfficeMlsId;
    const evaShopId = listing.EvaShopId;
    if (!shop || !listOfficeMlsId || !evaShopId) return true;

    const shopIdMatches = shop.shopId === listOfficeMlsId || shop.shopId === evaShopId;
    const shopGroupMatches =
      shop.shopGroup?.id === listOfficeMlsId || shop.shopGroup?.id === evaShopId;
    const someShopIdsMatch = shop.shops?.some(
      (s) => s.shopId === listOfficeMlsId || s.shopId === evaShopId,
    );

    return shopIdMatches || shopGroupMatches || someShopIdsMatch;
  };

  useEffect(() => {
    if (activeListings?.length > 0 && pastListings?.length > 0) {
      setTabs(['Active Listings', 'Past Listings']);
      setActive('Active Listings');
    } else if (activeListings?.length > 0) {
      setTabs(['Active Listings']);
      setActive('Active Listings');
    } else if (pastListings?.length > 0) {
      setTabs(['Past Listings']);
      setActive('Past Listings');
    }
  }, [activeListings?.length, pastListings?.length]);

  // Set Table Array
  const tableArray = active === 'Active Listings' ? activeListings : pastListings;

  // Sort by filter function
  function filterByFilter(filter) {
    if (filter == 'Price (Low to High)') {
      tableArray.sort((a, b) => ((a.ListPrice ?? 0) > (b.ListPrice ?? 0) ? 1 : -1));
    } else if (filter == 'Price (High to Low)') {
      tableArray.sort((a, b) => ((a.ListPrice ?? 0) < (b.ListPrice ?? 0) ? 1 : -1));
    } else if (filter == 'Beds/Baths (Ascending)') {
      tableArray.sort((a, b) => ((a.BedroomsTotal ?? 0) > (b.BedroomsTotal ?? 0) ? 1 : -1));
    } else {
      tableArray.sort((a, b) => ((a.BedroomsTotal ?? 0) < (b.BedroomsTotal ?? 0) ? 1 : -1));
    }
  }

  const numberOfPages = Math.ceil(totalResults / resultsPerPage);

  const toggleDropdown = () => {
    setOpenDropdown(!isOpenDropdown);
  };

  const hideToggleDropdown = () => {
    setOpenDropdown(false);
  };

  const originWithoutShopname = window.location.origin.replace(`://${shopname}.`, '://');

  return activeListings.length === 0 && pastListings.length === 0 ? null : (
    <Block narrow>
      <Header>{t('Unit Details')}</Header>
      <ButtonGroup>
        {tabs.length > 0
          ? tabs.map((type) => {
              return (
                <Tab
                  key={type}
                  $active={active === type}
                  onClick={() => {
                    setActive(type);
                  }}
                >
                  {type}
                </Tab>
              );
            })
          : null}
      </ButtonGroup>
      <FilterDropdown>
        <FilterText>
          {t('Sort by')}:&nbsp;
          <ClickAwayListener onClickAway={hideToggleDropdown}>
            <FilterToggle onClick={toggleDropdown}>
              <FilterLabel>{activeFilter}</FilterLabel>
              {isOpenDropdown ? <ChevronUp /> : <ChevronDown />}
            </FilterToggle>
          </ClickAwayListener>
        </FilterText>
        <DropdownContent className={isOpenDropdown ? 'show' : undefined}>
          {filters.map((filter) => (
            <Filter
              key={filter}
              $active={activeFilter === filter}
              onClick={() => {
                setFilter(filter);
                filterByFilter(filter);
                hideToggleDropdown();
              }}
            >
              {filter}
            </Filter>
          ))}
        </DropdownContent>
      </FilterDropdown>
      <HistoryTable>
        <thead>
          <tr>
            <th>{t('Address')}</th>
            <th>
              {t('Beds')}/{t('Baths')}
            </th>
            <th>{t('Price')}</th>
            <th>{t('Square Footage')}</th>
            <th>{t('Floorplan')}</th>
          </tr>
        </thead>
        {tableArray && (
          <tbody>
            {tableArray.map((listing) => {
              const listUrl = buildListUrl({
                StreetNumber: listing.StreetNumber,
                StreetName: listing.StreetName,
                City: listing.City,
                StateOrProvince: listing.StateOrProvince,
                ZipCode: listing.PostalCode,
                OriginatingSystemName: listing.OriginatingSystemName,
                ListingId: listing.ListingId,
              });

              const belongsToCurrentStore = getListingBelongsToCurrentStore(listing);
              const formattedListUrl =
                !belongsToCurrentStore && shopname ? originWithoutShopname + listUrl : listUrl;
              const listPrice = getListingPrice({
                originatingSystemName: listing.OriginatingSystemName,
                country: listing.Country,
                listingCurrency: listing.CurrencyCode,
                standardStatus: listing.StandardStatus,
                listPrice: listing.ListPrice!,
                closePrice: listing.ClosePrice,
                closePriceToDisplay: listing.ClosePriceToDisplay,
                custom14,
                custom15,
              });
              const address =
                getCompiledAddressForDevelopments(listing) ?? listing.UnparsedAddress ?? null;
              const floorPlanUrl = null;
              const sqFt = getSqFt(listing);
              const convertedArea = sqFt ? convertArea(sqFt, 'sq.ft') : null;

              return (
                <>
                  <tr>
                    <td>
                      <Text>
                        <ConditionallyWrapWithUrl
                          href={formattedListUrl}
                          target={!belongsToCurrentStore}
                        >
                          {address}
                        </ConditionallyWrapWithUrl>
                      </Text>
                    </td>
                    <td>{renderBedBath(listing)}</td>
                    <td>
                      <Text>{listPrice.formattedPrice}</Text>
                    </td>
                    <td>
                      <Text>{convertedArea ? convertedArea.formattedArea : t('N/A')}</Text>
                    </td>
                    <td>
                      <Text>
                        {floorPlanUrl ? <a href={floorPlanUrl}>{t('View Floorplan')}</a> : t('N/A')}
                      </Text>
                    </td>
                  </tr>
                </>
              );
            })}
          </tbody>
        )}
      </HistoryTable>
      <HistoryTableMobile>
        {tableArray &&
          tableArray.map((listing) => {
            const listUrl = buildListUrl({
              StreetNumber: listing.StreetNumber,
              StreetName: listing.StreetName,
              City: listing.City,
              StateOrProvince: listing.StateOrProvince,
              ZipCode: listing.PostalCode,
              OriginatingSystemName: listing.OriginatingSystemName,
              ListingId: listing.ListingId,
            });
            const belongsToCurrentStore = getListingBelongsToCurrentStore(listing);
            const formattedListUrl =
              !belongsToCurrentStore && shopname ? originWithoutShopname + listUrl : listUrl;
            const listPrice = getListingPrice({
              originatingSystemName: listing.OriginatingSystemName,
              country: listing.Country,
              listingCurrency: listing.CurrencyCode,
              standardStatus: listing.StandardStatus,
              listPrice: listing.ListPrice!,
              closePrice: listing.ClosePrice,
              closePriceToDisplay: listing.ClosePriceToDisplay,
              custom14,
              custom15,
            });
            const address =
              getCompiledAddressForDevelopments(listing) ?? listing.UnparsedAddress ?? null;
            const floorPlanUrl = null;
            const sqFt = getSqFt(listing);
            const convertedArea = sqFt ? convertArea(sqFt, 'sq.ft') : null;

            return (
              <tr>
                <td>
                  <Label>{t('Address')}</Label>
                  {address && (
                    <Text>
                      <ConditionallyWrapWithUrl
                        href={formattedListUrl}
                        target={!belongsToCurrentStore}
                      >
                        {address}
                      </ConditionallyWrapWithUrl>
                    </Text>
                  )}
                  <Label>{t('Price')}</Label>
                  <Text>{listPrice.formattedPrice}</Text>
                  <Text>
                    {floorPlanUrl ? <a href={floorPlanUrl}>{t('View Floorplan')}</a> : t('N/A')}
                  </Text>
                </td>
                <td>
                  <Label>
                    {t('Beds')}/{t('Baths')}
                  </Label>
                  {renderBedBath(listing)}
                  <Label>{t('Square Footage')}</Label>
                  <Text>{convertedArea ? convertedArea.formattedArea : t('N/A')}</Text>
                </td>
              </tr>
            );
          })}
      </HistoryTableMobile>
      {numberOfPages > 1 && (
        <PaginationContainer>
          <Pagination
            currentPage={currentPage}
            totalPages={numberOfPages}
            pageSelected={handlePageSelect}
          />
        </PaginationContainer>
      )}
    </Block>
  );
};

export default DevelopmentUnitDetails;
